<script>
	import { onMount } from 'svelte';
	import { Link } from "svelte-routing";
	import { environment } from '../js/utils/environment';
	import { pageOffset, universalScroll, toggleUniversalToPageScroll } from '../js/model/stores';

	import StartSection from '../sections/StartSection.svelte';
	import ShareSection from "../sections/ShareSection.svelte";
	import TopNavigation from "../components/TopNavigation.svelte";
	import BottomNavigation from "../components/BottomNavigation.svelte";

	export let name;


	// left: 37, up: 38, right: 39, down: 40,
	// spacebar: 32, pageup: 33, pagedown: 34, end: 35, home: 36
	const scrollKeys = {37: 1, 38: 1, 39: 1, 40: 1};

	// modern Chrome requires { passive: false } when adding event
	let supportsPassive = false;
	let wheelOpt;
	let wheelEvent;
	let lastTouchClientY;

	let _main;

	/**
	 *
	 * @param {*} event Can be either scroll, touchmove, keydown
	 */
	let calculatePageOffset = (event) => {

		let delta = 0;

		switch(event.type) {
			case 'touchstart':
				lastTouchClientY = event.touches[0].clientY;
				break;
			case 'touchmove':
				delta = (event.touches[0].clientY - lastTouchClientY);
				delta = delta * -1;		// touchmove direction value is contrary to scroll direction

				lastTouchClientY = event.touches[0].clientY;
				break;

			// Do nothing here but prevent the default case. We'll check the keyCode later
			case 'keydown':
				break;

			// All other events (scroll, wheel, mousewheel) should have the deltaY value
			default:
				delta = event.deltaY;
				break;
		}

		if (!event.keyCode) {
			event.preventDefault();
			let lastOffset = $pageOffset;
			let newOffset = Math.max(0, ($pageOffset + delta));

			// Stop scrolling down (positive delta)
			const mainBottomOffset = Math.floor(_main.getBoundingClientRect().bottom - environment().innerHeight());
			const footerHeight = document.querySelector('.bottom_navigation').getBoundingClientRect().height * 0.8;

			// FIXME: Freeze newOffset before doing the last update. Checking the main offset afterwards causes some overscrolling, depending on the scroll speed.

			if (delta > 0 && (mainBottomOffset + footerHeight) < 0) {
				return;
			}

			pageOffset.set(newOffset);
		}
		else if (scrollKeys[event.keyCode]) {
			event.preventDefault();

			switch (event.keyCode) {
				case 38:
					pageOffset.set($pageOffset - 10);
					break;
				case 40:
					pageOffset.set($pageOffset + 10);
					break;
			}
		}
	};

	let initScrollingMonitor = () => {
		try {
			window.addEventListener("test", null, Object.defineProperty({}, 'passive', {
				get: function () { supportsPassive = true; }
			}));
		} catch(e) {}

		wheelOpt = supportsPassive ? { passive: false } : false;
		wheelEvent = 'onwheel' in document.createElement('div') ? 'wheel' : 'mousewheel';

		// If the page loads with an window offset (on page reload), it must have been scrolled behind the universal scroll section.
		const initialPageOffset = window.scrollY > 0 ? window.scrollY + $toggleUniversalToPageScroll : 0;

		pageOffset.set(initialPageOffset);
		enableUniversalScrolling();
	};

	let enableUniversalScrolling = () => {
		window.addEventListener('DOMMouseScroll', calculatePageOffset, false); // older FF
		window.addEventListener(wheelEvent, calculatePageOffset, wheelOpt); // modern desktop
		window.addEventListener('touchstart', calculatePageOffset, wheelOpt); // mobile
		window.addEventListener('touchmove', calculatePageOffset, wheelOpt); // mobile
		window.addEventListener('keydown', calculatePageOffset, false);
	};

	let calculateCorrectViewportHeight = () => {
		// First we get the viewport height and we multiple it by 1% to get a value for a vh unit
		let vhInPixel = window.innerHeight * 0.01;

		// Then we set the value in the --vh custom property to the root of the document
		document.documentElement.style.setProperty('--vh', `${vhInPixel}px`);
	};

	let setBaseFontSizeByScreenWidth = () => {

		const BASE_SIZE = 1.2;	// used as the default 'vw' and with calculations

		const vwInPixel = window.innerWidth * 0.01;
		const baseSizeInPixel = vwInPixel * BASE_SIZE;

		if (baseSizeInPixel < 16) {
			document.documentElement.style.setProperty('--base-size', '16px');
		}
		else if (baseSizeInPixel > 30) {
			document.documentElement.style.setProperty('--base-size', '30px');
		}
		else {
			document.documentElement.style.setProperty('--base-size', `${BASE_SIZE}vw`);
		}
	};

	onMount(() => {
		initScrollingMonitor();
		calculateCorrectViewportHeight();
		setBaseFontSizeByScreenWidth();

		// We listen to the resize event
		window.addEventListener('resize', () => {
			calculateCorrectViewportHeight();
			setBaseFontSizeByScreenWidth();
		});

		pageOffset.subscribe((value) => {

			scroll({
				top: Math.max(0, (value - $toggleUniversalToPageScroll)),
				behavior: 'auto'	// instantly update the scroll position. Using 'smooth' should be used when jumping between anchors only!
			});

			if (value < $toggleUniversalToPageScroll) {
				universalScroll.set(true);
			}
			else {
				universalScroll.set(false);
			}
		});
	});
</script>

<style type="text/css">
	main {
		text-align: center;
		padding: 0;
		margin: 0 auto;
	}

	h1 {
		color: var(--color-dove-grey);
		text-transform: uppercase;
		font-size: 4em;
		font-weight: 100;
	}

	@media (min-width: 640px) {
		main {
			max-width: none;
		}
	}
</style>

<Link to="/">
	<TopNavigation route="confirmed"/>
</Link>
<main bind:this={_main}>
	<StartSection/>
	<ShareSection route="confirmed"/>
</main>
<BottomNavigation/>
